export default {
    nav: {
        home: '首页',
        about: '关于',
        generation: '生成',
        optimization: '优化',
        blog: '博客',
        github: 'GitHub'
    },
    hero: {
        title: 'KernelPilot',
        subtitle: '自动化、快速、高效的并行代码生成与优化工具',
        description: '将您的自然语言指令或串行代码转换为高度优化的并行实现。利用 AI 分析和代码生成功能，支持 CUDA、OpenMP 等并行计算框架。',
        cta: {
            generation: '代码生成',
            optimization: '代码优化',
            secondary: '了解详情'
        }
    },
    introduction: {
        title: '介绍',
        subtitle: '以速度、自动化和效率革新并行计算',
        description: 'Kernel Pilot 是一个尖端在线平台，旨在大幅简化和加速高性能并行实现的开发。它自动生成针对 CUDA、OpenMP 等并行计算框架优化的代码，将快速响应与深度硬件感知调优相结合。',
        items: {
            autoGen: {
                title: '全自动生成',
                description: '用户描述其计算问题——矩阵乘法、卷积、归约或自定义并行工作负载——KernelPilot 立即生成所需的并行代码。无需手动调优。'
            },
            optimization: {
                title: '极速性能',
                description: '每个生成的内核都经过精细调优，性能可匹配或超越典型的手写实现。平台优化线程和块配置、内存访问模式、共享内存使用、寄存器分配，甚至张量核心操作。'
            },
            multiPlatform: {
                title: '效率优先',
                description: 'KernelPilot 生成的代码精简高效——旨在最大化占用率、最小化线程分歧和内存瓶颈。结果：内核运行更快、更稳定，优于典型基准代码。'
            },
            analysis: {
                title: '工程师与研究者的理想之选',
                description: '无论您是 GPU 研究人员、AI 开发者还是系统集成商，KernelPilot 都能简化工作流程。让您避开繁琐的并行计算配置，专注于算法设计和性能目标。'
            }
        }
    },
    stats: {
        codeGenerated: '生成代码行数',
        optimizations: '优化次数',
        speedup: '平均加速比',
        users: '活跃用户'
    },
    about: {
        title: '关于 KernelPilot',
        description: 'KernelPilot 是一个先进的 AI 驱动平台，旨在帮助开发者创建和优化并行代码。我们的使命是让并行编程变得人人可及，无论是学习并行计算的学生还是从事高性能计算的资深工程师。',
        mission: {
            title: '我们的使命',
            content: '通过提供智能工具，消除串行和并行编程范式之间的鸿沟，让并行计算普及化。'
        },
        vision: {
            title: '我们的愿景',
            content: '让每一位开发者都能轻松驾驭并行计算的强大力量，无需面对陡峭的学习曲线。'
        }
    },
    versions: {
        title: '新闻动态',
        subtitle: '了解我们的最新进展与公告',
        readMore: '阅读更多',
        categories: {
            all: '全部',
            release: '发布',
            feature: '功能',
            research: '研究',
            tutorial: '教程'
        }
    },
    generation: {
        title: '代码生成',
        subtitle: '从自然语言描述生成优化的并行代码',
        inputPromptPlaceholder: '在此输入您的自然语言提示...',
        outputPlaceholder: '生成的并行代码将显示在这里...',
        buttons: {
            run: '生成'
        },
        options: {
            target_platform: '目标平台',
            target_hardware: '目标硬件',
            optimization: '优化级别'
        },
        labels: {
            inputPrompt: '输入提示词',
            outputCode: '输出代码'
        },
        status: {
            Ready: '就绪',
            Success: '成功',
            'Pre-processing': '预处理中',
            Generating: '生成中',
            Optimizing: '优化中',
            Profiling: '分析中',
            Fixing: '修复中',
            Finished: '已完成',
            Error: '错误'
        },
        results: {
            speedup: '预估加速比'
        }
    },
    optimization: {
        title: '代码优化',
        subtitle: '优化您现有的并行代码以获得更好的性能',
        inputCodePlaceholder: '在此输入您的串行代码...',
        inputTestDataPlaceholder: `在此输入您的测试数据，格式如下：
[Number of test cases]
[Number of variables]

[Variable 1 Name]
[Variable 1 Data]

[Variable 2 Name]
[Variable 2 Data]`,
        outputPlaceholder: '优化后的并行代码将显示在这里...',
        buttons: {
            run: '优化'
        },
        options: {
            target_platform: '目标平台',
            target_hardware: '目标硬件',
            optimization: '优化级别'
        },
        labels: {
            inputCode: '输入代码',
            inputTestData: '输入测试数据',
            outputCode: '输出代码'
        },
        status: {
            Ready: '就绪',
            Success: '成功',
            'Pre-processing': '预处理中',
            Generating: '生成中',
            Optimizing: '优化中',
            Profiling: '分析中',
            Fixing: '修复中',
            Finished: '已完成',
            Error: '错误'
        },
        results: {
            speedup: '预估加速比'
        }
    },
    footer: {
        tagline: '自动化、快速、高效的并行代码生成工具',
        copyright: '© 2025 Kernel Pilot 团队。保留所有权利。',
        quickLinks: '快速链接',
        connect: '联系方式',
        links: {
            privacy: '隐私政策',
            terms: '服务条款',
            contact: '联系我们'
        }
    },
    common: {
        loading: '加载中...',
        error: '发生错误',
        retry: '重试',
        back: '返回',
        next: '下一步',
        submit: '提交',
        cancel: '取消',
        save: '保存',
        delete: '删除',
        edit: '编辑',
        view: '查看',
        search: '搜索',
        filter: '筛选',
        sort: '排序',
        language: '语言'
    },
    contact: {
        suggestions: {
            title: '建议与反馈'
        },
        collaboration: {
            title: '学术与产业合作'
        }
    }
}
